import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogMain, DialogFooter, DialogTitle, DialogDescription } from '@ncoder/ui';
import type { SetMemberBudgetInput } from '@/services/org-admin';

interface BudgetFormValues {
  amount_usd: string;
}

export interface MemberBudgetEditModalTarget {
  user_id: string;
  email: string | null;
  budget_usd: number;
}

export interface MemberBudgetEditModalProps {
  open: boolean;
  member: MemberBudgetEditModalTarget | null;
  isSubmitting: boolean;
  onSubmit: (data: SetMemberBudgetInput) => void;
  onClose: () => void;
}

export function MemberBudgetEditModal({ open, member, isSubmitting, onSubmit, onClose }: MemberBudgetEditModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<BudgetFormValues>({
    defaultValues: { amount_usd: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open || !member) return;
    reset({ amount_usd: String(member.budget_usd) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member, reset]);

  const handleCancel = () => onClose();

  const submit = handleSubmit((data) => {
    onSubmit({ amount_usd: Number(data.amount_usd) });
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Edit monthly budget</DialogTitle>
          <DialogDescription className="text-xs">
            {member?.email ?? member?.user_id} — enforcement is per-seat: once spend reaches this amount, LiteLLM
            blocks further requests until the cycle resets.
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="budget-amount" className="text-xs font-medium text-foreground">
              Monthly budget (USD)
            </label>
            <input
              id="budget-amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="10.00"
              {...register('amount_usd', {
                required: 'Enter an amount',
                validate: (value) => {
                  const n = Number(value);
                  return (!Number.isNaN(n) && n >= 0) || 'Enter a non-negative amount';
                },
              })}
              disabled={isSubmitting}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {errors.amount_usd && <p className="text-xs text-destructive">{errors.amount_usd.message}</p>}
          </div>
        </DialogMain>

        <DialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!isValid || isSubmitting}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {isSubmitting ? 'Saving…' : 'Save budget'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
